import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import { type CaseType, primaryLiableVehicle } from "@recobra/schemas/case";
import type { TaskCaseSummary as TaskCaseSummaryData } from "@recobra/schemas/task";
import { Skeleton } from "@recobra/ui/skeleton";

import { CaseDriverVehicleCell } from "../../cases";
import { TruncatedText } from "../../lib";
import { TaskSummaryList, TaskSummaryRow } from "./TaskLayout";

const taskCaseSummaryRows = (caseType: CaseType | undefined) => [
  "client" as const,
  caseType === "debt_collection" ? ("debtor" as const) : ("claimant" as const),
  ...(caseType === undefined || caseType === "third_party_claim" ? ["thirdParty" as const] : []),
];

export const TaskCaseSummary: FC<{
  kase: TaskCaseSummaryData;
  labelColumn?: "auto" | "fixed";
}> = ({ kase, labelColumn }) => {
  const liable = primaryLiableVehicle(kase.liableVehicles);
  const rowContent = {
    client: {
      label: m.task_field_context_client(),
      value: (
        <TruncatedText
          text={
            [kase.clientName, kase.branchName].filter(Boolean).join(" — ") || m.common_em_dash()
          }
        />
      ),
    },
    claimant: {
      label: m.cases_col_claimant_data(),
      value: <CaseDriverVehicleCell vehicle={kase.vehicle} />,
    },
    thirdParty: {
      label: m.cases_col_third_party_data(),
      value: (
        <CaseDriverVehicleCell
          vehicle={liable.vehicle}
          additionalVehicleCount={liable.additionalCount}
        />
      ),
    },
    debtor: {
      label: m.case_section_debtor_title(),
      value: <TruncatedText text={kase.debtorName || m.common_em_dash()} />,
    },
  };

  return (
    <TaskSummaryList labelColumn={labelColumn}>
      {taskCaseSummaryRows(kase.caseType).map((row) => (
        <TaskSummaryRow key={row} label={rowContent[row].label}>
          {rowContent[row].value}
        </TaskSummaryRow>
      ))}
    </TaskSummaryList>
  );
};

export const TaskCaseSummarySkeleton: FC<{
  caseType?: CaseType;
  labelColumn?: "auto" | "fixed";
}> = ({ caseType, labelColumn }) => (
  <TaskSummaryList labelColumn={labelColumn}>
    {taskCaseSummaryRows(caseType).map((row) => (
      <TaskSummaryRow key={row} label={<Skeleton className="h-3 w-20" />}>
        <Skeleton className="h-3 w-40" />
      </TaskSummaryRow>
    ))}
  </TaskSummaryList>
);
